import { Check, Plus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useSession, type BusinessType } from '../lib/session';
import { BUSINESS_TYPES, SETUP_STEPS, setupDone, STATUS_STYLE } from '../lib/stores';
import { syncNow, useSync } from '../lib/sync';
import { toast } from '../lib/toast';
import { Button, Field, Sheet, SkeletonRows } from './ui';
import { useUi } from './ui-store';
import { tr } from '../lib/i18n';

interface StoreRow { id: string; store_no: string; name: string; business_type: BusinessType; setup_steps: string[]; kyc_status: string; status: string; current: boolean }

/** Owner's store list: switch between stores or add a new one. */
export function StoreSwitcher() {
  const { stores: open, set } = useUi();
  const switchStore = useSession((s) => s.switchStore);
  const [list, setList] = useState<StoreRow[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', businessType: 'supermarket' as BusinessType, copy: true });
  const [max, setMax] = useState(10);
  const close = () => { set({ stores: false }); setAdding(false); };

  useEffect(() => {
    if (!open) return;
    setList(null);
    api<{ stores: StoreRow[]; max?: number }>('/api/stores').then((r) => { setList(r.stores); if (r.max) setMax(r.max); }, (e) => { toast.error(e.message); close(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (id: string) => {
    setBusy(id);
    try {
      // Bills belong to the store they were made in: upload them before leaving.
      await syncNow();
      if (useSync.getState().pending > 0) throw new Error('Some bills are not synced yet. Connect to the internet and try again.');
      await switchStore(id);
    } catch (e) { toast.error((e as Error).message); setBusy(null); }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('new');
    try {
      const current = list?.find((s) => s.current)?.id;
      const r = await api<{ id: string; store_no: string; copied: number }>('/api/stores', { method: 'POST', json: { name: form.name, businessType: form.businessType, ...(form.copy && current ? { copyFrom: current } : {}) } });
      toast.ok(tr('Store #{0} created{1}', { 0: r.store_no, 1: r.copied ? ` · ${r.copied} products copied` : '' }));
      await go(r.id);
    } catch (err) { toast.error((err as Error).message); setBusy(null); }
  };

  return (
    <Sheet open={open} onClose={close} title={adding ? tr('Add a store') : tr('Your stores')}>
      {!adding ? (
        <div className="flex flex-col gap-2 pb-2">
          {!list && <SkeletonRows rows={2} />}
          {list?.map((s) => {
            const t = BUSINESS_TYPES.find((b) => b.id === s.business_type) ?? BUSINESS_TYPES[0];
            return (
              <button key={s.id} disabled={!!busy} onClick={() => (s.current ? close() : void go(s.id))}
                className={`flex items-center gap-3 rounded-3xl border p-3 text-left ${s.current ? 'border-forest-700 bg-forest-50' : 'border-line hover:bg-paper'}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-t-green text-primary"><t.icon size={22} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold">{s.name}</span>
                  <span className="block text-xs text-muted"><span className="num">#{s.store_no}</span> {tr('· {0} · setup', { 0: t.label })} {setupDone(s.setup_steps)}/{SETUP_STEPS.length}{s.status && s.status !== 'approved' && <span className={`ml-1.5 rounded-full px-2 py-0.5 font-bold capitalize ${STATUS_STYLE[s.status] ?? ''}`}>{s.status}</span>}</span>
                </span>
                {s.current ? <span className="flex items-center gap-1 rounded-full bg-forest-700 px-3 py-1 text-xs font-bold text-white"><Check size={14} />{tr('Open')}</span>
                  : <span className="rounded-full border border-line px-3 py-1 text-xs font-bold">{busy === s.id ? tr('Switching…') : tr('Switch')}</span>}
              </button>
            );
          })}
          <Button variant="secondary" className="mt-2" disabled={(list?.length ?? 0) >= max} onClick={() => setAdding(true)}><Plus size={18} />{tr('Add a store')}</Button>
          <p className="text-center text-xs text-muted">{tr('{0} of {1} stores on this account', { 0: list?.length ?? 0, 1: max })}</p>
        </div>
      ) : (
        <form onSubmit={add} className="flex flex-col gap-4 pb-2">
          <Field label={tr('Store name')} required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tr('Green Basket – Indiranagar')} />
          <div className="grid grid-cols-3 gap-2">
            {BUSINESS_TYPES.map((t) => (
              <button type="button" key={t.id} aria-pressed={form.businessType === t.id} onClick={() => setForm({ ...form, businessType: t.id })}
                className={`flex h-16 flex-col items-center justify-center gap-1 rounded-3xl border text-xs font-bold ${form.businessType === t.id ? 'border-forest-700 bg-forest-50' : 'border-line'}`}>
                <t.icon size={18} />{t.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-3 rounded-2xl bg-forest-50 p-3 text-sm font-bold"><input type="checkbox" className="h-5 w-5 accent-primary" checked={form.copy} onChange={(e) => setForm({ ...form, copy: e.target.checked })} />{tr('Copy products, offers and suppliers from this store')}</label>
          <p className="text-xs text-muted">{tr('Each store is separate: its own store number, catalog, prices, stock, staff, customers and bills. New stores are checked once before billing opens.')}</p>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setAdding(false)}>{tr('Back')}</Button><Button disabled={busy === 'new'}>{busy === 'new' ? tr('Creating…') : tr('Create & open')}</Button></div>
        </form>
      )}
    </Sheet>
  );
}
